/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Select, Selector, SelectorOptions, State, StateContext } from '@ngxs/store';
import {
    DataFieldValueModel,
    DataFormDefinition, DataFormField,
    DataFormModel,
    DataFormStateModel,
    DataFormValueModel,
    LoadDataFormPayload
} from './dataForm.model';
import {
    ClearDataFormList,
    LoadDataForm,
    LoadDataFormList,
    PrefillFormFields,
    UpdateFormFieldValue,
} from './dataForm.actions';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { RestService } from '../../shared/services/rest.service';
import { LoggerService } from '../../shared/services/logger.service';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthState } from '../../../core/security/state/authState.state';
import { Injectable } from '@angular/core';

@State<DataFormStateModel>({
    name: 'dataForm',
    defaults: {
        names: [],
        definitions: {},
    },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class DataFormState implements NgxsOnInit {
    @Select(AuthState.validated) validated$: Observable<boolean>;

    constructor(private logger: LoggerService, private rest: RestService) {}

    ngxsOnInit({ dispatch }: StateContext<any>): void | any {
        this.validated$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                tap(() => setTimeout(() => dispatch(new LoadDataFormList()), 0))
            )
            .subscribe();
    }

    @Action(LoadDataFormList)
    loadDataFormList({ getState, setState }: StateContext<DataFormStateModel>) {
        return this.rest.apiGet<string[]>('dataform/list').pipe(
            tap((formNames) => {
                const state = DashGisUtil.clone(getState());
                state.names = formNames || [];
                setState(state);
            })
        );
    }

    @Action(ClearDataFormList)
    clearDataFormList({ getState, setState }: StateContext<DataFormStateModel>) {
        const state = DashGisUtil.clone(getState());
        state.names = [];
        state.definitions = {};
        setState(state);
    }

    @Action(LoadDataForm)
    loadDataForm({ getState, setState }, action: LoadDataForm) {
        return this.loadDataFormInternal({ getState, setState }, action.payload);
    }

    @Action(UpdateFormFieldValue)
    updateFieldValue({ getState, setState }, action: UpdateFormFieldValue) {
        const state = DashGisUtil.clone<DataFormStateModel>(getState());
        const formName = action.payload.formName;
        const formData = action.payload.data;
        const valueModel = state.definitions[formName].value;
        const valueField = valueModel.fields.find((f) => f.name === formData.name);
        valueField.value = formData.value;
        valueField.dirty = formData.dirty;
        setState(state);
    }

    @Action(PrefillFormFields)
    prefillFormFields({ getState, setState }, action: PrefillFormFields) {
        const state = DashGisUtil.clone<DataFormStateModel>(getState());
        const formName = action.payload.formName;
        action.payload.data.forEach((formData) => {
            const definitionField = state.definitions[formName].definition.fields.find(
                (f) => f.fieldName === formData.name
            );
            if (definitionField) {
                definitionField.currentValue = formData.value;
                definitionField.newValue = formData.value;
            }
            const valueField = state.definitions[formName].value.fields.find((f) => f.name === formData.name);
            if (valueField) {
                valueField.value = formData.value;
            }
        });
        setState(state);
    }

    private loadDataFormInternal({ getState, setState }, payload: LoadDataFormPayload): Observable<DataFormDefinition> {
        const state: DataFormStateModel = DashGisUtil.clone(getState());
        const params = {
            keys: payload.keys.join(','),
        };
        if (payload.initialSelection) {
            params['initialselection'] = JSON.stringify(payload.initialSelection);
        }
        return this.rest.apiGet<DataFormDefinition>(`dataform/definition/${payload.name}`, params).pipe(
            map((definition) => DataFormState.transformData(definition)),
            tap((definition) => {
                const formState: DataFormModel = {} as DataFormModel;
                formState.definition = definition;
                formState.keys = payload.keys;
                const valueModel: DataFormValueModel = DataFormState.createValueModel(definition);
                valueModel.keys = payload.keys;
                formState.value = valueModel;
                state.definitions[payload.name] = formState;
                setState(state);
            })
        );
    }

    @Selector([DataFormState])
    static names(state: DataFormStateModel): string[] {
        return state.names;
    }

    @Selector([DataFormState])
    static definitions(state: DataFormStateModel) {
        return state.definitions;
    }

    @Selector([DataFormState.definitions])
    static definition(definitions: { [key: string]: DataFormModel }): (string) => DataFormDefinition {
        return (name: string) => {
            if (definitions[name]) {
                return definitions[name].definition;
            } else {
                return null;
            }
        };
    }

    @Selector([DataFormState])
    static keys(state: DataFormStateModel) {
        return (name: string) => {
            if (state.definitions[name]) {
                return state.definitions[name].keys;
            } else {
                return null;
            }
        };
    }

    @Selector([DataFormState.definitions])
    static value(definitions: { [key: string]: DataFormModel }) {
        return (name: string) => {
            if (definitions[name]) {
                return definitions[name].value;
            } else {
                return null;
            }
        };
    }

    @Selector([DataFormState.definitions])
    static field(definitions: { [key: string]: DataFormModel }) {
        return (formName: string, fieldName: string) => {
            if (definitions[formName]) {
                const definition = definitions[formName].definition;
                const field = definition.fields.find((f) => f.fieldName === fieldName);
                if (field !== null) {
                    return field;
                }
            }
            return null;
        };
    }

    private static createValueModel(def: DataFormDefinition): DataFormValueModel {
        const formValueModel = {} as DataFormValueModel;
        formValueModel.name = def.name;
        formValueModel.fields = def.fields.map(
            (f) =>
                ({
                    name: f.fieldName,
                    value: f.currentValue,
                    dirty: false,
                } as DataFieldValueModel)
        );
        return formValueModel;
    }

    private static transformData(definition: DataFormDefinition) {
        // change dates to js Date
        for (const field of definition.fields) {
            if (field.dataType === 'date') {
                if (field.currentValue) {
                    field.currentValue = new Date(field.currentValue as string);
                }
            }
            if (field.uiType === 'dropdown_multi') {
                field.currentValue = field.currentValue.toString().split(',');
            }
        }
        return definition;
    }
}
